import { RefObject, useEffect, useState } from 'react';
import { GenericLiveStream } from 'types/stream';
import { Player } from '../../../types';

type HookProps = {
  player: Player | null;
  canInject: boolean;
  playerRef: RefObject<HTMLDivElement>;
  stream?: GenericLiveStream | null;
};

const createButton = () => {
  const liveBtn = document.createElement('div');
  liveBtn.classList.add('live_btn', 'active');

  return liveBtn;
};

const getPanel = (playerRef: RefObject<HTMLDivElement>) => playerRef.current?.querySelector('.plyr__controls');
const isDuration = (player: Player) => !!(player.media.duration && player.media.duration.toString() !== 'Infinity');

const useLiveButton = ({ player, stream, canInject, playerRef }: HookProps) => {
  const [btn, setBtn] = useState<HTMLDivElement>();

  const enableLive = () => activateLive();

  const activateLive = (canMove = true) => {
    if (!player || !btn) return;

    if (canMove) {
      player.media.currentTime = isDuration(player)
        ? player.media.duration - 1
        : player.media.seekable.end(player.media.seekable.end.length - 1) - 1.0;
    }

    btn.classList.add('active');
    setTimeout(() => player.paused && player.play(), 100);
  };

  const onPause = () => {
    if (!btn) return;

    btn.classList.remove('active');
  };

  const onSeek = () => {
    if (!btn || !player) return;

    const canDestroyLive = isDuration(player)
      ? Math.round(player.media.duration) - Math.round(player.media.duration) <= 1
      : true;

    if (canDestroyLive) {
      btn.classList.remove('active');
    } else activateLive(false);
  };

  useEffect(() => {
    if (!player || !playerRef.current) return;

    const panel = getPanel(playerRef);
    const canRemoveLiveButton = stream?.state === 'FINISHED' && !!btn && !player?.playing;

    if (canRemoveLiveButton) {
      panel?.querySelector('.live_btn')?.remove();
    }
  }, [stream?.state, btn, playerRef.current, player?.playing]);

  useEffect(() => {
    const panel = getPanel(playerRef);
    const dwnl = playerRef.current?.querySelector('[data-plyr=download]');
    const isAlreadyExists = playerRef.current?.querySelector('.live-btn');

    if (!panel || isAlreadyExists || !canInject || !player || !dwnl) {
      return;
    }

    const btnWrapper = createButton();
    panel.insertBefore(btnWrapper, dwnl);

    setBtn(btnWrapper);
  }, [playerRef.current, canInject, player]);

  useEffect(() => {
    if (!btn || !player) return;

    player.onseek = onSeek;
    player.on('pause', onPause);
    btn.addEventListener('click', enableLive);
    btn.addEventListener('touchend', enableLive);

    return () => {
      player.off('pause', onPause);
      btn.removeEventListener('click', enableLive);
      btn.removeEventListener('touchend', enableLive);
    };
  }, [btn, player]);
};

export default useLiveButton;
